import { MODEL_UI } from '../../copy/model-configuration'
import { ArrowRight, Check, ChevronDown } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { t } from '../../copy'
import { T } from '../../copy/text'
import {
  activeMentionQuery,
  applyMention,
  draftKey,
  deriveMentions,
  mentionCandidates,
  mentionInvitablePeople,
  mentionPeople,
  type DerivedMention,
  teamBarShape,
  composerPlaceholder,
} from './composer'
import { BIND_KIND_COPY, bindCardBlocksInput, bindRequirement } from './intent'
import { PlanCard } from './plan-card'
import { AttachButton, AttachmentTray } from './attachment-tray'
import { buildSlashCommands, filterSlashCommands, slashQuery, type SlashCommand } from './commands'
import { canSend, sendableIDs, type DraftAttachment } from './attachments'
import type { WirePlan } from './plan'
import type { LuluSkill } from './queries'
import {
  DEFAULT_CHOICE,
  EFFORTS,
  EFFORT_COPY,
  affectsTeam,
  interruptsRunning,
  mayUserPick,
  reasonTextFor,
  toneForStatus,
  type ChatModelChoice,
  type Effort,
  type ModelOption,
} from './model-picker'
import { resolveDisplayName, type HumanMember, type TeamMember } from './roster'
import type { SidecarTab } from './sidecar'
import { VoiceInput } from './voice-input'

/**
 * 输入框上方那一条。设计稿 §5：**两种形态互斥**，虚线是单 Agent、
 * 实线是团队，点整条打开 Sidecar 团队页签。
 *
 * 形态由 `teamBarShape` 算，组件只管画——所以「拿不到就不猜」那条
 * 有纯函数用例守着，不依赖渲染。
 */

/**
 * 一行模型/一档强度长什么样。
 *
 * 选中态原来**一个 class 都没有**——`data-active` 只写进 DOM 属性，
 * 用例查得到，而屏幕上看不出来。三档强度尤其明显：快速/标准/最高
 * 三个裸词并排，选了哪一档只有它自己知道。
 *
 * 高度给到 min-h-9：这一列在手机上要用手指点，原来的 py-1 只有二十几个像素。
 */
function modelRowClass(selected: boolean): string {
  const base =
    'flex min-h-9 w-full items-center justify-between gap-2 rounded-[10px] px-2.5 text-left text-[13px] font-medium disabled:opacity-40' /* npc-token-allow: Pattern A pop row */
  return selected
    ? `${base} lu-pop-item-selected`
    : `${base} text-npc-lulu-t2 hover:bg-npc-lulu-bg-1`
}

/** 「固定一个」折叠区里的行：更密，但仍是 Soft Structuralism 圆角选中态。 */
function fixedRowClass(selected: boolean): string {
  const base =
    'lu-fixed-row flex min-h-8 w-full items-center justify-between gap-2 rounded-[10px] px-2.5 text-left text-[12.5px] font-medium disabled:opacity-40' /* npc-token-allow: Pattern A denser fixed row */
  return selected
    ? `${base} lu-pop-item-selected`
    : `${base} text-npc-lulu-t2 hover:bg-npc-lulu-bg-1`
}

/**
 * 同一个 id 只留一行。
 *
 * 服务端今天会把同一个模型从两个来源各送一次（一次带正经名字、一次只有
 * 原始 id），单子上就出现「Kimi K3」与「kimi-k3」并排——**看起来像坏了一半**。
 * 真正的修法在服务端，这里先按 id 去重，且**留带名字的那一条**：
 * 两条指向同一个模型，留哪条都能用，而留有名字的那条读得懂。
 */
function dedupeModels(models: readonly ModelOption[]): readonly ModelOption[] {
  const byID = new Map<string, ModelOption>()
  for (const model of models) {
    const seen = byID.get(model.id)
    if (seen === undefined || (seen.name === seen.id && model.name !== model.id)) {
      byID.set(model.id, model)
    }
  }
  return [...byID.values()]
}

export function TeamBar({
  mode,
  members,
  soloAgentName,
  onOpenSidecar,
  onChangeAgent,
}: {
  readonly mode: 'solo' | 'team' | null
  readonly members: readonly TeamMember[] | null
  readonly soloAgentName: string | null
  readonly onOpenSidecar: (tab: SidecarTab) => void
  readonly onChangeAgent: () => void
}) {
  const shape = teamBarShape({ mode, members, soloAgentName })
  if (shape.kind === 'none') return null

  if (shape.kind === 'solo') {
    return (
      <button
        type="button"
        data-testid="lulu-teambar"
        data-shape="solo"
        onClick={onChangeAgent}
        className="mb-2 flex w-full items-center gap-2 rounded-npc-chip border border-dashed border-npc-lulu-line-2 bg-transparent px-3 py-1.5 text-left text-npc-caption text-npc-lulu-t3"
      >
        <T id="lulu_workbench.teambar.solo" vars={{ agent: shape.agent }} />
      </button>
    )
  }

  return (
    <button
      type="button"
      data-testid="lulu-teambar"
      data-shape="team"
      onClick={() => onOpenSidecar('team')}
      className="mb-2 flex w-full items-center gap-2 rounded-npc-chip border border-solid border-npc-lulu-line-2 bg-npc-lulu-bg-2 px-3 py-1.5 text-left text-npc-caption text-npc-lulu-t2"
    >
      <span className="min-w-0 truncate">
        <T
          id="lulu_workbench.teambar.team"
          vars={{ mode: MODEL_UI.ui_254, count: String(shape.count) }}
        />
      </span>
      <span className="flex-1" />
      <span data-testid="lulu-teambar-adjust" className="shrink-0 text-npc-lulu-t3">
        <T id="lulu_workbench.teambar.adjust" />
      </span>
    </button>
  )
}

/**
 * 输入框。带 `@` 面板与按会话保管的草稿。
 *
 * 草稿写进 `sessionStorage` 而不是组件 state：**验收 #22 要的是
 * 「开关 Sidecar、切页签之后草稿还在」**，而那几个动作都可能让这棵子树
 * 重新挂载。存在组件里的话，一次重挂就没了，而表现是「我的字没了」。
 *
 * 不用 localStorage：草稿是这一次会话里没说完的话，不该跨浏览器会话
 * 留在别人的机器上。
 */
export function Composer({
  conversationId,
  projectId,
  mode,
  members,
  soloAgentName,
  composerModel,
  onOpenSidecar,
  onChangeAgent,
  onSend,
  joinable,
  people,
  invitablePeople,
  onInvitePerson,
  onJoinTeam,
  models,
  onPickModel,
  seed,
  asSheet,
  link,
  messageCount,
  bindTargets,
  onBindProject,
  replyAskedForProject,
  plan,
  archived,
  justScheduledTaskId,
  attachments,
  onPickAttachment,
  onRetryAttachment,
  onRemoveAttachment,
  attachmentNotice,
  onStartWork,
  startingWork,
  startWorkFailed,
  onStop,
  skills,
  onNewConversation,
  onRename,
}: {
  readonly conversationId: string | null
  /** 挂了哪个项目。`null` = 通用会话，绑定卡据此决定要不要出。 */
  readonly projectId: string | null
  /**
   * 这条会话此刻那份算好的计划（「开始做」那张卡）。
   * `undefined` 还没读到、`null` 读失败——两者都不画卡。
   */
  readonly plan?: WirePlan | null
  readonly archived?: boolean
  readonly justScheduledTaskId?: string | null
  /**
   * 这一条草稿上挂着的附件（四态都在里面）。
   *
   * 不给 = 这一屏不支持附件，那一排与那颗按钮整个不画——
   * **不是画一个禁用的占位**。
   */
  readonly attachments?: readonly DraftAttachment[]
  readonly onPickAttachment?: () => void
  readonly onRetryAttachment?: (localId: string) => void
  readonly onRemoveAttachment?: (localId: string) => void
  readonly attachmentNotice?: 'too_large' | 'bad_type' | 'too_many' | null
  /** 真发「安排开发」。不给就整张卡不画（点不动的按钮是错的形状）。 */
  readonly onStartWork?: () => void
  readonly startingWork?: boolean
  readonly startWorkFailed?: boolean
  /**
   * 叫停正在跑的这一轮（Esc）。**只在真有一轮在跑时给**；没给，Esc 什么都不做。
   * 与等回话那一行的「停下」按钮走同一条（cancel），条件也由同一处判。
   */
  readonly onStop?: () => void
  /**
   * GET /skills 的投影，给「/」那份清单挑技能。`null` 读不到 / `undefined` 还没读——
   * 两种都只是「技能那几条不列」，别的照列。
   */
  readonly skills?: readonly LuluSkill[] | null
  /** 「/」清单里的「新开一条话头」。不给就没有那一条。 */
  readonly onNewConversation?: () => void
  /** 「/改名 新名字」真发 PATCH。不给就没有那一条。回 Promise，失败在这里说。 */
  readonly onRename?: (title: string) => Promise<void>
  readonly mode: 'solo' | 'team' | null
  readonly members: readonly TeamMember[] | null
  readonly soloAgentName: string | null
  /** 服务端给的当前对话模型。`null` = 读不到，界面不画当前值。 */
  readonly composerModel: {
    readonly policy: 'auto' | 'fixed'
    readonly profile_id?: string
    readonly model_id?: string
  } | null
  readonly onOpenSidecar: (tab: SidecarTab) => void
  readonly onChangeAgent: () => void
  /** 真发一条消息。回 Promise，发完由调用方清草稿。 */
  /** 第三个参数是他这一条选的推理强度（快速 / 标准 / 最高），随消息一起发。 */
  /** 第四个参数是「这一条只出计划，别开工」（no_auto_start）。 */
  /** 第五个参数是他在这句话里 @ 到的那些人（结构化，带字节偏移）。 */
  readonly onSend?: (
    body: string,
    attachmentIDs: readonly string[],
    effort: Effort | null,
    planOnly: boolean,
    mentions: readonly DerivedMention[],
  ) => Promise<void>
  /**
   * Agent 库里还没进这条会话的那些。给了才在 @ 面板里列「叫进来」那一组。
   *
   * 与 `members` 分开：合成一个列表的那天，一个还没进来的候选会被
   * 当成在席成员算进人数里（TeamPanel 那侧同一条理由）。
   */
  readonly joinable?: readonly TeamMember[]
  /** 这条会话里的人。@ 他们只是叫来看，不产生任何执行（PRD §6）。 */
  readonly people?: readonly HumanMember[]
  /** 「一起管」里还没进这条会话的人。@ 面板列得出他们，点一下先叫进来再 @ 上。 */
  readonly invitablePeople?: readonly { readonly userId: string; readonly label: string }[]
  readonly onInvitePerson?: (userId: string) => Promise<void>
  /** 真把一位叫进这条会话。**不给就不列那一组**——没有落点的选项比没有选项糟。 */
  readonly onJoinTeam?: (agentProfileId: string) => Promise<void>
  /** 可选的模型列表。`null` = 读不到，下拉里只有「Lulu 自动选」。 */
  readonly models?: readonly ModelOption[] | null
  /** 真把选择持久化下去。不给就点不动。 */
  readonly onPickModel?: (policy: 'auto' | 'fixed', modelId: string | undefined) => Promise<void>
  /**
   * 空态里点的那条起手式。非空时填进输入框并聚焦。
   *
   * **不覆盖已经写了一半的草稿**：他已经在打字了还被一句模板顶掉，
   * 比没有这个功能糟得多。
   */
  readonly seed?: string
  /**
   * 可以绑的项目。`null` = 读不到（卡上说读不到，不画一个空列表冒充「没有项目」）。
   * 不给这一项就只出说明文字——**不做一颗点不动的「绑定」**。
   */
  /**
   * 手机那一档。真时模型选择器画成**底部 sheet**（设计稿 mModelSheet），
   * 不是桌面那个 260px 下拉。
   *
   * 不自己读 window.innerWidth：那样组件会有两套关于「现在多宽」的答案，
   * 而布局那套已经在 tracks 里算过一次了。
   */
  readonly asSheet?: boolean
  /** 连着没有。断线那一档的提示语要说「你的话不会丢」，别的档说不出这句。 */
  readonly link?: 'live' | 'down'
  /** 这条会话已经有几条消息。0 = 还没说过话，提示语不一样。 */
  readonly messageCount?: number
  readonly bindTargets?: readonly { readonly id: string; readonly name: string }[] | null
  /** 真绑。回 Promise，失败由这里显示，不吞。 */
  readonly onBindProject?: (projectId: string) => Promise<void>
  /** Lulu 上一句是不是在要项目。见 bindRequirement 里那段。 */
  readonly replyAskedForProject?: boolean
}) {
  const key = draftKey(conversationId)
  const ref = useRef<HTMLTextAreaElement | null>(null)
  const pickableModels = useMemo(() => dedupeModels(models ?? []), [models])
  const [text, setText] = useState('')
  const [caret, setCaret] = useState(0)
  const [effort, setEffort] = useState<Effort | null>(DEFAULT_CHOICE.effort)
  const [sending, setSending] = useState(false)
  // **归一成数组一次，别在下面每处各写一个 ?? []。**
  // 漏一处就是运行时崩，而那一处多半是最少走到的那一条分支。
  const attachmentItems = attachments ?? []
  const sendable = canSend(text, attachmentItems)
  const [sendFailed, setSendFailed] = useState(false)
  const [modelOpen, setModelOpen] = useState(false)
  /** 「固定一个」次级区：默认收起，避免长名单主导 Pattern A。已固定时自动展开。 */
  const [fixedOpen, setFixedOpen] = useState(false)
  const [modelFailed, setModelFailed] = useState(false)
  const [binding, setBinding] = useState(false)
  const [bindFailed, setBindFailed] = useState(false)
  const [bindDismissed, setBindDismissed] = useState(false)
  const [effortOpen, setEffortOpen] = useState(false)
  // 「/」清单里此刻亮着的那一条；查询一变就回到第一条。
  const [slashIndex, setSlashIndex] = useState(0)
  const [renameFailed, setRenameFailed] = useState(false)
  // 「这一条只出计划，别开工」。只管下一条消息，发成功就复位——与推理强度同一条规矩。
  const [planOnly, setPlanOnly] = useState(false)

  // 这条会话此刻的模型选择。policy 来自服务端，推理强度是本地的
  // ——后端没有这个字段，所以它只影响下一条消息，界面上也这么说。
  const choice: ChatModelChoice = {
    mode: composerModel?.policy ?? DEFAULT_CHOICE.mode,
    modelId: composerModel?.profile_id ?? null,
    effort,
  }
  const selectedModelName =
    choice.modelId === null
      ? null
      : (models?.find((model) => model.id === choice.modelId)?.name ??
        composerModel?.model_id ??
        choice.modelId)
  /** 芯片显示「标准」时，弹层里对应 pill 也要 coral soft fill（贴 Pattern A comps）。 */
  const displayEffort: Effort = effort ?? 'standard'

  /**
   * 选一个模型。**真发 POST 并持久化。**
   *
   * 失败时**不改本地显示**——本地先改再发的话，一次失败会留下
   * 一个「界面说固定了、服务端还是自动」的分歧，而下一条消息
   * 会用服务端那个，用户完全看不出来。
   */
  const closeModelPicker = useCallback(() => {
    setModelOpen(false)
    setFixedOpen(false)
    setEffortOpen(false)
  }, [])

  const pickModel = useCallback(
    async (policy: 'auto' | 'fixed', modelId: string | undefined) => {
      if (!onPickModel) return
      setModelFailed(false)
      try {
        await onPickModel(policy, modelId)
        // Successful primary action may dismiss Pattern A.
        setModelOpen(false)
        setFixedOpen(false)
      } catch {
        setModelFailed(true)
      }
    },
    [onPickModel],
  )

  // Pattern A dismiss contract (design spec): outside/backdrop click MUST close,
  // plus Escape / chip toggle / explicit close / successful pick.
  useEffect(() => {
    if (!modelOpen && !effortOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      e.preventDefault()
      closeModelPicker()
    }
    const onOutside = (e: MouseEvent) => {
      const t = e.target
      if (!(t instanceof Node)) return
      const menu = document.querySelector('[data-testid="lulu-model-menu"]')
      const effortSheet = document.querySelector('[data-testid="lulu-effort-sheet"]')
      const chip = document.querySelector('[data-testid="lulu-model-pick"]')
      if (menu && menu.contains(t)) return
      if (effortSheet && effortSheet.contains(t)) return
      if (chip && chip.contains(t)) return // chip toggle owns its own click
      closeModelPicker()
    }
    window.addEventListener('keydown', onKey)
    document.addEventListener('mousedown', onOutside)
    return () => {
      window.removeEventListener('keydown', onKey)
      document.removeEventListener('mousedown', onOutside)
    }
  }, [modelOpen, effortOpen, closeModelPicker])

  // 要不要在消息流里出绑定卡。**规则判定，零模型。**
  const detectedBind = bindRequirement({ text, projectId, replyAskedForProject })
  // 点过「先不绑」就这一轮不再出。**不是永久关掉**——
  // 他下一句又说到代码时该再问一次，那是新的一次意图。
  const bindKind = bindDismissed ? null : detectedBind

  // 挂载时把草稿读回来。切会话时 key 变，读的是那条会话自己的草稿。
  useEffect(() => {
    let saved: string | null = null
    try {
      saved = window.sessionStorage.getItem(key)
    } catch {
      // 隐私模式下 sessionStorage 会抛。草稿没了是遗憾，界面崩了是故障。
      saved = null
    }
    setText(saved ?? '')
  }, [key])

  const write = useCallback(
    (next: string) => {
      setText(next)
      setBindDismissed(false)
      try {
        window.sessionStorage.setItem(key, next)
      } catch {
        /* 同上 */
      }
    },
    [key],
  )

  // 起手式：只在输入框是空的时候填。
  useEffect(() => {
    if (seed === undefined || seed === '') return
    setText((prev) => {
      if (prev.trim() !== '') return prev
      try {
        window.sessionStorage.setItem(key, seed)
      } catch {
        /* 隐私模式，同上 */
      }
      return seed
    })
    ref.current?.focus()
  }, [seed, key])

  /**
   * 真发。**按钮与 Enter 走同一个函数**——两条路各写一份的话，
   * 「只有成功才清草稿」这条规矩迟早只有一边守着。
   */
  const submit = async (bodyOverride?: string) => {
    const body = bodyOverride ?? text
    const ok = bodyOverride === undefined ? sendable.ok : canSend(body, attachmentItems).ok
    if (!onSend || !ok || sending) return
    setSendFailed(false)
    setSending(true)
    try {
      // **从要发出去的那段文本推**，不是从他点过谁推：
      // 屏幕上有什么，发出去的就是什么（deriveMentions 顶上那段）。
      await onSend(body, sendableIDs(attachmentItems), effort, planOnly, deriveMentions(body, members, people))
      // **只有成功才清。** 失败时清掉等于把用户打的话吃掉，
      // 而他连重发都做不到——那比没发出去糟得多。
      write('')
      // 推理强度「仅下一条消息；发送成功后复位」——界面上就是这么写的。「只出计划」同理。
      setEffort(DEFAULT_CHOICE.effort)
      setPlanOnly(false)
    } catch {
      setSendFailed(true)
    } finally {
      setSending(false)
    }
  }

  // 「/」清单。归档的话头不接受写，那份清单也就没有意义。
  const slash = archived ? null : slashQuery(text)
  const slashHead = slash?.head ?? null
  const slashArg = slash?.arg ?? ''
  const slashItems: SlashCommand[] =
    slash === null
      ? []
      : filterSlashCommands(
          buildSlashCommands(
            {
              projectId,
              running: onStop !== undefined,
              skills,
              canOpenNew: onNewConversation !== undefined,
              canRename: onRename !== undefined && conversationId !== null,
              planOnly,
            },
            slashArg,
          ),
          slashHead ?? '',
        )
  useEffect(() => {
    setSlashIndex(0)
    setRenameFailed(false)
  }, [slashHead, slashArg])
  const slashActive = slashItems.length === 0 ? -1 : Math.min(slashIndex, slashItems.length - 1)

  /**
   * 真做那一条。每一条都对着一个已有的东西；这里没有任何一条是「先记着」。
   * 做完清掉草稿：那串「/…」是叫菜单的，不是要发的话。
   */
  const runSlash = async (cmd: SlashCommand) => {
    if (!cmd.ready) return
    switch (cmd.action.kind) {
      case 'effort':
        setEffort(cmd.action.effort)
        write('')
        break
      case 'stop':
        write('')
        onStop?.()
        break
      case 'skill':
        write('')
        await submit(cmd.action.utterance)
        break
      case 'new':
        write('')
        onNewConversation?.()
        break
      case 'plan_only':
        setPlanOnly(cmd.action.on)
        write('')
        break
      case 'rename': {
        if (!onRename) return
        setRenameFailed(false)
        try {
          await onRename(cmd.action.title)
          write('')
        } catch {
          setRenameFailed(true)
        }
        break
      }
    }
    ref.current?.focus()
  }

  const query = activeMentionQuery(text, caret)
  const candidates = useMemo(
    () => (query === null ? [] : mentionCandidates(members, query)),
    [members, query],
  )
  /**
   * Agent 库里**还没进这条会话**的那些。
   *
   * 在这一步之前，@ 面板只列在席的人。而「我在 Agent 库里加了一位」与
   * 「他进了这条会话」是两件事，界面从来没说过——arron 2026-09-06 加了
   * buger 之后 @ 他，面板里根本没有他，那三个字就只是三个字。
   *
   * 叫进来只作用于这条会话，不改 Agent 库（服务端 handleJoinTeam 的原话）。
   */
  const joinCandidates = useMemo(
    () => (query === null || onJoinTeam === undefined ? [] : mentionCandidates(joinable ?? null, query)),
    [joinable, query, onJoinTeam],
  )
  /**
   * 这条会话里的人。**与上面两组分开算**：@ 他们不产生任何执行，
   * 只是叫他来看一眼（PRD §6）。合成一组的话，两种完全不同的后果
   * 就只能靠调用处的 if 分开，而那种 if 会在加一条新来源时悄悄失效。
   */
  const peopleCandidates = useMemo(
    () => (query === null ? [] : mentionPeople(people, query)),
    [people, query],
  )
  /**
   * 「一起管」里还没进这条会话的人。
   *
   * **@ 面板必须列得出他们**，否则打 `@他的名字` 只是几个字——
   * deriveMentions 只认在席的人，服务端根本收不到这次 @。
   * 2026-09-06 Agent 那侧栽过一次（那段注释还在下面），
   * 2026-09-08 人这侧又栽了一次：arron @ 了一位老板，他什么都没收到。
   */
  const invitePeopleCandidates = useMemo(
    () =>
      query === null || onInvitePerson === undefined
        ? []
        : mentionInvitablePeople(invitablePeople, query),
    [invitablePeople, query, onInvitePerson],
  )
  /** 正在叫谁进来（他的 profile id）。null = 没在叫。 */
  const [joining, setJoining] = useState<string | null>(null)
  const [joinFailed, setJoinFailed] = useState(false)
  const open = query !== null && mode === 'team'

  /** 把一位已经在席的名字写进输入框。 */
  function pickMention(label: string) {
    const out = applyMention({ text, caret, name: label })
    write(out.text)
    setCaret(out.caret)
    ref.current?.focus()
  }

  /**
   * 先把他叫进这条会话，再 @ 上。
   *
   * **顺序不能反。** 先写字后加人的话，中间那一刻消息可能已经发出去了，
   * 而那时他还不是参与方——服务端认不出这条 @，这一轮回落给 Lulu，
   * 也就是这一整条改动要修的那件事本身。
   */
  async function bringIn(agentProfileId: string, label: string) {
    if (onJoinTeam === undefined) return
    setJoinFailed(false)
    setJoining(agentProfileId)
    try {
      await onJoinTeam(agentProfileId)
      pickMention(label)
    } catch {
      // **不写字。** 叫失败了还把「@小柯 」插进去，他会以为叫到了。
      setJoinFailed(true)
    } finally {
      setJoining(null)
    }
  }

  /**
   * 先把他叫进这条会话，再 @ 上。
   *
   * **顺序不能反**，与上面那条 bringIn 一模一样的理由：先写字后加人的话，
   * 中间那一刻消息可能已经发出去了，而那时他还不是参与方——
   * deriveMentions 认不出他，这次 @ 又只是几个字。
   */
  async function bringPersonIn(userId: string, label: string) {
    if (onInvitePerson === undefined) return
    setJoinFailed(false)
    setJoining(userId)
    try {
      await onInvitePerson(userId)
      pickMention(label)
    } catch {
      // **不写字。** 叫失败了还把「@阿码 」插进去，他会以为叫到了。
      setJoinFailed(true)
    } finally {
      setJoining(null)
    }
  }

  return (
    <div data-testid="lulu-composer" className="lu-composer-shell relative">
      {/* 「这一条只出计划」那颗标签：在输入框上方，点它取消。不出现 = 没拨。 */}
      {planOnly ? (
        <button
          type="button"
          data-testid="lulu-plan-only-chip"
          onClick={() => setPlanOnly(false)}
          className="mb-2 inline-flex items-center gap-1 rounded-npc-chip border border-npc-lulu-line-2 bg-npc-lulu-bg-2 px-2 py-0.5 text-npc-meta text-npc-lulu-t2"
        >
          <T id="lulu_workbench.composer.plan_only_chip" />
          <span aria-hidden="true">×</span>
        </button>
      ) : null}
      {/* 附件那一排在输入框上方，与绑定卡、「开始做」那张卡同一层。 */}
      <AttachmentTray
        items={attachmentItems}
        onRetry={onRetryAttachment}
        onRemove={onRemoveAttachment}
        notice={attachmentNotice ?? null}
      />
      <TeamBar
        mode={mode}
        members={members}
        soloAgentName={soloAgentName}
        onOpenSidecar={onOpenSidecar}
        onChangeAgent={onChangeAgent}
      />

      {/* 「开始做」那张卡。放在绑定卡**前面**：绑定卡说的是「你得先绑一个
          项目」，而这张卡只在已经绑了项目时才出——两张卡不会同时出现，
          顺序只影响读代码的人。放这儿是因为它属于「这一轮要干什么」，
          与下面那些「这条会话是什么」不是一类。 */}
      <PlanCard
        plan={plan}
        projectId={projectId}
        archived={Boolean(archived)}
        justScheduledTaskId={justScheduledTaskId}
        onStart={onStartWork}
        onRevise={onStartWork === undefined ? undefined : () => ref.current?.focus()}
        starting={startingWork}
        failed={startWorkFailed}
      />

      {/* 绑定卡。**在消息流里，不是 Modal** —— 冻结合同写死的。
          bindCardBlocksInput 恒 false 且没有入参，所以这里没有
          「这次比较重要就拦住他」那一支可写。 */}
      {bindKind !== null ? (
        <div
          data-testid="lulu-bind-card"
          data-kind={bindKind}
          data-blocking={bindCardBlocksInput() ? 'yes' : 'no'}
          className="mb-2 rounded-npc-card border border-npc-lulu-line-2 bg-npc-lulu-bg-2 p-2"
        >
          <p className="text-npc-caption text-npc-lulu-t2">
            <T
              id="lulu_workbench.bind.needed"
              vars={{ what: t(BIND_KIND_COPY[bindKind] as 'lulu_workbench.bind.kind_code') }}
            />
          </p>
          <p className="mt-1 text-npc-meta text-npc-lulu-t3">
            <T id="lulu_workbench.bind.keep_chatting" />
          </p>
          <p className="text-npc-meta text-npc-lulu-t3">
            <T id="lulu_workbench.bind.one_way" />
          </p>

          {/*
            **选一个项目，然后真的绑。**

            上一版这张卡只有三句说明——它把「你得先绑一个项目」讲清楚了，
            然后不给任何绑的办法。文案 `bind.pick`（选一个项目）早就在
            文案库里，而前端一处都没用：又一处先写好的话配一个没做出来的控件。

            `bindTargets === null` 是**读不到**，不是「没有项目」：
            这时说读不到，不画一个空列表冒充「你一个项目都没有」。
          */}
          {onBindProject === undefined ? null : (
            <div className="mt-2">
              <p className="text-npc-meta text-npc-lulu-t3">
                <T id="lulu_workbench.bind.pick" />
              </p>
              {bindTargets === null ? (
                <p
                  data-testid="lulu-bind-unreachable"
                  className="mt-1 text-npc-meta text-npc-lulu-warn"
                >
                  <T
                    id="lulu_workbench.gap.unreachable"
                    vars={{ what: t('lulu_workbench.bind.pick') }}
                  />
                </p>
              ) : (
                <div className="mt-1 flex flex-wrap gap-1.5">
                  {(bindTargets ?? []).map((target) => (
                    <button
                      key={target.id}
                      type="button"
                      data-testid="lulu-bind-target"
                      disabled={binding}
                      onClick={() => {
                        setBindFailed(false)
                        setBinding(true)
                        void onBindProject(target.id)
                          .catch(() => setBindFailed(true))
                          .finally(() => setBinding(false))
                      }}
                      className="h-[26px] rounded-npc-chip border border-npc-lulu-line-2 bg-npc-lulu-bg-1 px-2.5 text-npc-meta text-npc-lulu-t1 disabled:opacity-50" /* npc-token-allow: 设计稿卡内小按钮高 26px */
                    >
                      <T id="lulu_workbench.bind.do" vars={{ name: target.name }} />
                    </button>
                  ))}
                  <button
                    type="button"
                    data-testid="lulu-bind-skip"
                    onClick={() => setBindDismissed(true)}
                    className="h-[26px] rounded-npc-chip px-2.5 text-npc-meta text-npc-lulu-t3" /* npc-token-allow: 同上 */
                  >
                    <T id="lulu_workbench.bind.skip" />
                  </button>
                </div>
              )}
              {bindFailed ? (
                <p data-testid="lulu-bind-failed" className="mt-1 text-npc-meta text-npc-lulu-bad">
                  <T id="lulu_workbench.bind.failed" />
                </p>
              ) : null}
            </div>
          )}
        </div>
      ) : null}

      {open ? (
        <div
          data-testid="lulu-mention-panel"
          className="absolute bottom-full left-0 z-10 mb-1 w-[260px] rounded-npc-card border border-npc-lulu-line-2 bg-npc-lulu-bg-2 p-2 shadow-npc-popover" /* npc-token-allow: 设计稿 @ 面板 260px 固定宽 */
        >
          <p className="px-1 pb-1 text-npc-meta text-npc-lulu-t3">
            <T id="lulu_workbench.mention.title" />
          </p>
          {candidates.length === 0 &&
          joinCandidates.length === 0 &&
          peopleCandidates.length === 0 &&
          invitePeopleCandidates.length === 0 ? (
            <p
              data-testid="lulu-mention-empty"
              className="px-1 py-1 text-npc-meta text-npc-lulu-t3"
            >
              <T id="lulu_workbench.mention.empty" />
            </p>
          ) : (
            <>
              {/* 已经在席的那些。**分组标题只在两组都有的时候出现**——
                  只有一组时那行标题是纯噪音。 */}
              {candidates.length > 0 ? (
                <>
                  {joinCandidates.length > 0 ? (
                    <p className="px-1 pt-0.5 text-npc-meta text-npc-lulu-t4">
                      <T id="lulu_workbench.mention.group_here" />
                    </p>
                  ) : null}
                  <ul>
                    {candidates.map((m) => {
                      const label = resolveDisplayName(m)
                      if (label === null) return null
                      return (
                        <li key={m.id}>
                          <button
                            type="button"
                            data-testid="lulu-mention-option"
                            onClick={() => pickMention(label)}
                            className="w-full rounded-npc-chip-inline px-1 py-1 text-left text-npc-caption text-npc-lulu-t2"
                          >
                            {label}
                          </button>
                        </li>
                      )
                    })}
                  </ul>
                </>
              ) : null}

              {/* 库里还没进这条会话的那些。点一下 = 先把他叫进来，再 @ 上。
                  在这一步之前，Agent 库里的人在 @ 面板上根本不存在——
                  arron 2026-09-06 加了 buger 之后 @ 他，面板里没有他，
                  于是那三个字只是三个字。 */}
              {joinCandidates.length > 0 ? (
                <>
                  {candidates.length > 0 ? (
                    <p className="mt-1 px-1 pt-0.5 text-npc-meta text-npc-lulu-t4">
                      <T id="lulu_workbench.mention.group_library" />
                    </p>
                  ) : null}
                  <ul data-testid="lulu-mention-joinable">
                    {joinCandidates.map((m) => {
                      const label = resolveDisplayName(m)
                      if (label === null) return null
                      return (
                        <li key={m.id}>
                          <button
                            type="button"
                            data-testid="lulu-mention-join"
                            disabled={joining !== null}
                            onClick={() => void bringIn(m.id, label)}
                            className="w-full rounded-npc-chip-inline px-1 py-1 text-left text-npc-caption text-npc-lulu-t2 disabled:opacity-50"
                          >
                            {joining === m.id ? t('lulu_workbench.mention.joining') : label}
                          </button>
                        </li>
                      )
                    })}
                  </ul>
                </>
              ) : null}
              {/* 一起管这条的人。**@ 他们不产生任何执行**——
                  只是叫他来看一眼。这条说明常驻，因为「@ 了没反应」
                  与「@ 了不该有反应」在屏幕上长得一模一样。 */}
              {peopleCandidates.length > 0 ? (
                <>
                  <p className="mt-1 px-1 pt-0.5 text-npc-meta text-npc-lulu-t4">
                    <T id="lulu_workbench.mention.group_people" />
                  </p>
                  <ul data-testid="lulu-mention-people">
                    {peopleCandidates.map((person) => (
                      <li key={person.id}>
                        <button
                          type="button"
                          data-testid="lulu-mention-person"
                          data-user-id={person.id}
                          onClick={() => pickMention(person.name)}
                          className="w-full rounded-npc-chip-inline px-1 py-1 text-left text-npc-body-sm text-npc-lulu-t1 hover:bg-npc-lulu-bg-2"
                        >
                          {person.name}
                        </button>
                      </li>
                    ))}
                  </ul>
                  <p
                    data-testid="lulu-mention-person-hint"
                    className="px-1 pt-0.5 text-npc-meta text-npc-lulu-t4"
                  >
                    <T id="lulu_workbench.mention.person_hint" />
                  </p>
                </>
              ) : null}
              {/* 还没进这条会话的人：**先叫进来，再 @ 上**。
                  顺序不能反——先写字后加人的话，中间那一刻消息可能已经
                  发出去了，而那时他还不是参与方，这次 @ 就又只是几个字。 */}
              {invitePeopleCandidates.length > 0 ? (
                <>
                  <p className="mt-1 px-1 pt-0.5 text-npc-meta text-npc-lulu-t4">
                    <T id="lulu_workbench.mention.group_invite_people" />
                  </p>
                  <ul data-testid="lulu-mention-invite-people">
                    {invitePeopleCandidates.map((who) => (
                      <li key={who.userId}>
                        <button
                          type="button"
                          data-testid="lulu-mention-invite-person"
                          data-user-id={who.userId}
                          disabled={joining === who.userId}
                          onClick={() => void bringPersonIn(who.userId, who.label)}
                          className="w-full rounded-npc-chip-inline px-1 py-1 text-left text-npc-body-sm text-npc-lulu-t1 hover:bg-npc-lulu-bg-2 disabled:opacity-60"
                        >
                          {joining === who.userId ? (
                            <T id="lulu_workbench.people.inviting" />
                          ) : (
                            who.label
                          )}
                        </button>
                      </li>
                    ))}
                  </ul>
                </>
              ) : null}

              {joinFailed ? (
                <p
                  data-testid="lulu-mention-join-failed"
                  className="px-1 py-0.5 text-npc-meta text-npc-lulu-bad"
                >
                  <T id="lulu_workbench.mention.join_failed" />
                </p>
              ) : null}
            </>
          )}
          {/* 两句边界写在面板里，因为用户就是在这一刻可能以为「叫一声 = 加人」。 */}
          <p className="mt-1 border-t border-npc-lulu-line-1 px-1 pt-1 text-npc-meta text-npc-lulu-t3">
            <T id="lulu_workbench.mention.no_team_change" />
          </p>
          {/* 同一位 Agent 常常同时坐在两个项目的话头里，而他就是在这一刻
              决定要不要再把人叫进来一条。这句话是真的：上文按 conversation_id
              读，账户级长期记忆刻意不给这条路（mention_reply.go）。 */}
          <p data-testid="lulu-mention-own-memory" className="px-1 text-npc-meta text-npc-lulu-t3">
            <T id="lulu_workbench.mention.own_memory" />
          </p>
          <p className="px-1 text-npc-meta text-npc-lulu-t3">
            <T id="lulu_workbench.mention.sub_excluded" />
          </p>
        </div>
      ) : null}

      {slash !== null ? (
        <div
          data-testid="lulu-slash-menu"
          className="absolute bottom-full left-0 z-10 mb-1 w-[320px] rounded-npc-card border border-npc-lulu-line-2 bg-npc-lulu-bg-2 p-2 shadow-npc-popover" /* npc-token-allow: 与 @ 面板同一档固定宽，多 60px 装得下提示那一行 */
        >
          <p className="px-1 pb-1 text-npc-meta text-npc-lulu-t3">
            <T id="lulu_workbench.slash.title" />
          </p>
          {slashItems.length === 0 ? (
            <p data-testid="lulu-slash-empty" className="px-1 py-1 text-npc-meta text-npc-lulu-t3">
              <T id="lulu_workbench.slash.empty" />
            </p>
          ) : (
            <ul role="listbox" aria-label={t('lulu_workbench.slash.title')}>
              {slashItems.map((cmd, i) => (
                <li key={cmd.id}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={i === slashActive}
                    aria-disabled={!cmd.ready}
                    data-testid="lulu-slash-option"
                    data-command={cmd.id}
                    data-active={i === slashActive ? 'yes' : 'no'}
                    data-ready={cmd.ready ? 'yes' : 'no'}
                    onMouseEnter={() => setSlashIndex(i)}
                    onClick={() => void runSlash(cmd)}
                    className={
                      i === slashActive
                        ? 'flex w-full flex-col items-start rounded-npc-chip-inline bg-npc-lulu-bg-3 px-2 py-1 text-left'
                        : 'flex w-full flex-col items-start rounded-npc-chip-inline px-2 py-1 text-left hover:bg-npc-lulu-bg-3'
                    }
                  >
                    <span
                      className={
                        cmd.ready
                          ? 'text-npc-caption font-medium text-npc-lulu-t1'
                          : 'text-npc-caption font-medium text-npc-lulu-t3'
                      }
                    >
                      {cmd.label}
                    </span>
                    <span className="text-npc-meta text-npc-lulu-t3">{cmd.hint}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          {renameFailed ? (
            <p data-testid="lulu-slash-rename-failed" className="px-1 pt-1 text-npc-meta text-npc-lulu-bad">
              <T id="lulu_workbench.slash.rename_failed" />
            </p>
          ) : null}
          <p className="px-1 pt-1 text-npc-meta text-npc-lulu-t4">
            <T id="lulu_workbench.slash.keys" />
          </p>
        </div>
      ) : null}

      <textarea
        ref={ref}
        data-testid="lulu-input"
        value={text}
        placeholder={t(
          composerPlaceholder({
            link: link ?? 'live',
            projectId,
            messageCount: messageCount ?? 0,
          }),
        )}
        rows={2}
        onChange={(e) => {
          write(e.target.value)
          setCaret(e.target.selectionStart ?? e.target.value.length)
        }}
        onKeyUp={(e) => setCaret(e.currentTarget.selectionStart ?? 0)}
        onClick={(e) => setCaret(e.currentTarget.selectionStart ?? 0)}
        onKeyDown={(e) => {
          const composing = e.nativeEvent.isComposing || e.nativeEvent.keyCode === 229
          // 「/」清单开着的时候，上下键挑、Enter 用、Esc 收——按键先归它。
          if (slash !== null) {
            if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
              if (slashItems.length === 0) return
              e.preventDefault()
              const down = e.key === 'ArrowDown'
              setSlashIndex((i) => {
                const cur = Math.min(i, slashItems.length - 1)
                return down ? Math.min(cur + 1, slashItems.length - 1) : Math.max(cur - 1, 0)
              })
              return
            }
            if (e.key === 'Escape') {
              e.preventDefault()
              write('')
              return
            }
            if (e.key === 'Enter' && !e.shiftKey && !composing && slashActive >= 0) {
              e.preventDefault()
              const picked = slashItems[slashActive]
              if (picked) void runSlash(picked)
              return
            }
          }
          // Esc：Pattern A 弹层开着时先收弹层；否则 = 「停下」。
          if (e.key === 'Escape') {
            if (modelOpen || effortOpen) {
              e.preventDefault()
              closeModelPicker()
              return
            }
            if (onStop) {
              e.preventDefault()
              onStop()
            }
            return
          }
          // Enter 发、Shift+Enter 换行——与 Claude Code / Codex 同一套手感。
          // **手机档不接**：软键盘上的回车是换行，发送靠按钮（设计稿 mKb）。
          if (e.key !== 'Enter' || asSheet) return
          // 输入法正在选字时的回车是「选中这个词」，不是「发」。
          // isComposing 是标准说法；229 是 Safari 在合成刚结束那一下给的 keyCode。
          if (composing) return
          // 带任何修饰键的都让给浏览器：Shift+Enter 换行，别的不猜。
          if (e.shiftKey || e.altKey || e.ctrlKey || e.metaKey) return
          e.preventDefault()
          void submit()
        }}
        className="w-full resize-none border-0 bg-transparent px-0 py-0 text-[14px] font-medium leading-[1.5] text-npc-lulu-t1 outline-none" /* npc-token-allow: after-v4-taste composer textarea */
      />

      {/* Soft Structuralism B · Pattern A：一颗芯片「策略 · 力度」→ 同一弹层。 */}
      <div className="relative mt-2.5 flex items-center justify-between gap-2">
        <div className="relative flex min-w-0 items-center gap-2">
          <button
            type="button"
            data-testid="lulu-model-pick"
            data-policy={choice.mode}
            data-open={modelOpen ? 'yes' : 'no'}
            onClick={() => {
              setModelOpen((v) => {
                const next = !v
                setFixedOpen(next && (composerModel?.policy ?? 'auto') === 'fixed')
                return next
              })
              setEffortOpen(false)
            }}
            className="lu-model-chip"
          >
            {choice.mode === 'fixed' && selectedModelName ? (
              <span className="max-w-[120px] truncate">{selectedModelName}</span>
            ) : (
              <T id="lulu_workbench.model.auto" />
            )}
            <span className="lu-sep" aria-hidden>
              ·
            </span>
            <T
              id={
                (EFFORT_COPY[effort ?? 'standard'] as 'lulu_workbench.model.effort_fast')
              }
            />
            <ChevronDown aria-hidden className="size-[11px] opacity-70" strokeWidth={1.6} />
          </button>

          {/* 兼容旧用例：桌面也挂着 lulu-effort-pick 标记（力度在弹层里）。 */}
          <span data-testid="lulu-effort-pick" className="sr-only" aria-hidden>
            {effort ?? 'standard'}
          </span>

          {modelOpen ? (
            <>
              {asSheet ? (
                <button
                  type="button"
                  data-testid="lulu-model-backdrop"
                  aria-label={t('ui.close')}
                  className="lu-model-scrim"
                  onClick={closeModelPicker}
                />
              ) : null}
            <div
              data-testid="lulu-model-menu"
              data-dismiss-outside="yes"
              data-shape={asSheet ? 'sheet' : 'dropdown'}
              className={
                asSheet
                  ? 'fixed inset-x-0 bottom-0 z-30 max-h-[70vh] overflow-y-auto rounded-t-npc-card border-t border-npc-lulu-line-2 bg-npc-lulu-bg-2 p-3 shadow-npc-popover'
                  : 'lu-model-menu absolute bottom-full left-0 z-10 mb-2.5'
              }
            >
              {/* Pattern A · 选择策略（主区贴 comps；固定名单收进次级 disclosure） */}
              <div className="lu-pop-sec">
                <p
                  data-testid="lulu-model-title"
                  className="lu-pop-label"
                >
                  <T id="lulu_workbench.model.picker_title" />
                </p>
                <p className="lu-pop-helper">
                  <T id="lulu_workbench.model.picker_sub" />
                </p>
                {/* 「默认」分组标签对 Pattern A 是噪音；保留 testid/文案给契约，视觉上藏起来。 */}
                <p
                  data-testid="lulu-model-group"
                  data-group="default"
                  className="sr-only"
                >
                  <T id="lulu_workbench.model.group_default" />
                </p>
                <button
                  type="button"
                  role="option"
                  aria-selected={choice.mode === 'auto'}
                  data-testid="lulu-model-option"
                  data-model="auto"
                  data-selected={choice.mode === 'auto' ? 'yes' : 'no'}
                  onClick={() => void pickModel('auto', undefined)}
                  className={modelRowClass(choice.mode === 'auto')}
                >
                  <span className="min-w-0 truncate">
                    <T id="lulu_workbench.model.auto" />
                  </span>
                  {choice.mode === 'auto' ? (
                    <Check aria-hidden className="size-3.5 shrink-0" style={{ color: 'var(--lu-accent)' }} />
                  ) : null}
                </button>

                {(models ?? []).length === 0 ? null : (
                  <div className="lu-fixed-sec">
                    <button
                      type="button"
                      data-testid="lulu-model-fixed-disclosure"
                      aria-expanded={fixedOpen}
                      onClick={() => setFixedOpen((v) => !v)}
                      className={
                        choice.mode === 'fixed'
                          ? 'lu-fixed-disclosure lu-fixed-disclosure-on'
                          : 'lu-fixed-disclosure'
                      }
                    >
                      <span className="min-w-0 truncate text-left">
                        <span
                          data-testid="lulu-model-group"
                          data-group="fixed"
                          className="lu-fixed-disclosure-label"
                        >
                          <T id="lulu_workbench.model.group_fixed" />
                        </span>
                        {choice.mode === 'fixed' && selectedModelName ? (
                          <span className="lu-fixed-disclosure-summary">
                            {' '}
                            · {selectedModelName}
                          </span>
                        ) : null}
                      </span>
                      <ChevronDown
                        aria-hidden
                        className={
                          fixedOpen
                            ? 'size-[12px] shrink-0 opacity-70 rotate-180'
                            : 'size-[12px] shrink-0 opacity-70'
                        }
                        strokeWidth={1.6}
                      />
                    </button>
                    {fixedOpen ? (
                      <div
                        data-testid="lulu-model-fixed-list"
                        className="lu-fixed-list"
                      >
                        <p
                          data-testid="lulu-model-runtime-note"
                          className="lu-runtime-note"
                        >
                          <T id="lulu_workbench.model.runtime_not_wired" />
                        </p>
                        <p className="lu-runtime-note lu-runtime-note-secondary">
                          <T id="lulu_workbench.model.scope_note" />
                        </p>
                        {pickableModels.map((m) => {
                          const picked = choice.mode === 'fixed' && choice.modelId === m.id
                          return (
                            <button
                              key={m.id}
                              type="button"
                              role="option"
                              aria-selected={picked}
                              data-testid="lulu-model-option"
                              data-model={m.id}
                              data-status={m.status}
                              data-selected={picked ? 'yes' : 'no'}
                              data-unnamed={m.name === m.id ? 'yes' : 'no'}
                              data-pickable={mayUserPick(m) ? 'yes' : 'no'}
                              disabled={!mayUserPick(m)}
                              onClick={() => void pickModel('fixed', m.id)}
                              className={fixedRowClass(picked)}
                            >
                              <span
                                className={
                                  m.name === m.id
                                    ? 'min-w-0 truncate font-npc-mono text-npc-lulu-t3'
                                    : 'min-w-0 truncate'
                                }
                              >
                                {m.name}
                              </span>
                              <span className="flex shrink-0 items-center gap-1">
                                {m.status === 'ok' ? null : (
                                  <span
                                    data-testid="lulu-model-option-reason"
                                    className={[
                                      'truncate rounded-npc-tag-sm px-1.5 py-0.5 text-npc-meta',
                                      toneForStatus(m.status) === 'bad'
                                        ? 'bg-npc-lulu-bad/10 text-npc-lulu-bad'
                                        : 'bg-npc-lulu-warn/10 text-npc-lulu-warn',
                                    ].join(' ')}
                                  >
                                    {reasonTextFor(m) ?? t('lulu_workbench.model.unverified')}
                                  </span>
                                )}
                                {picked ? (
                                  <Check
                                    aria-hidden
                                    className="size-3.5"
                                    style={{ color: 'var(--lu-accent)' }}
                                  />
                                ) : null}
                              </span>
                            </button>
                          )
                        })}
                      </div>
                    ) : null}
                  </div>
                )}
              </div>

              {/* Pattern A · 本次消息力度 */}
              <div className="lu-pop-sec">
                <p className="lu-pop-label">
                  <T id="lulu_workbench.model.effort_sheet_title" />
                </p>
                <div
                  role="radiogroup"
                  aria-label={t('lulu_workbench.model.effort_sheet_title')}
                  className="lu-effort-row"
                >
                  {EFFORTS.map((e) => (
                    <button
                      key={e}
                      type="button"
                      role="radio"
                      aria-checked={displayEffort === e}
                      data-testid="lulu-effort-option"
                      data-effort={e}
                      data-active={displayEffort === e ? 'yes' : 'no'}
                      onClick={() => setEffort(e)}
                      className={
                        displayEffort === e ? 'lu-effort lu-effort-on' : 'lu-effort'
                      }
                    >
                      <T id={EFFORT_COPY[e] as 'lulu_workbench.model.effort_fast'} />
                    </button>
                  ))}
                </div>
              </div>

              <p
                data-testid="lulu-model-scope"
                data-affects-team={affectsTeam() ? 'yes' : 'no'}
                data-interrupts={interruptsRunning() ? 'yes' : 'no'}
                className="lu-pop-foot"
              >
                <T id="lulu_workbench.model.next_message_only" />
              </p>
              {asSheet ? (
                <div className="mt-2 flex items-center justify-between gap-2 px-1">
                  <p
                    data-testid="lulu-model-sheet-foot"
                    className="min-w-0 flex-1 text-npc-meta text-npc-lulu-t4"
                  >
                    <T id="lulu_workbench.model.sheet_foot" />
                  </p>
                  <button
                    type="button"
                    data-testid="lulu-model-close"
                    className="lu-btn-ghost shrink-0"
                    onClick={closeModelPicker}
                  >
                    <T id="ui.close" />
                  </button>
                </div>
              ) : null}
              {modelFailed ? (
                <p data-testid="lulu-model-failed" className="px-2.5 text-npc-meta text-npc-lulu-bad">
                  <T id="lulu_workbench.model.wont_switch" />
                </p>
              ) : null}
            </div>
            </>
          ) : null}

          {/* 窄屏：力度仍可经独立 sheet 打开（兼容旧 asSheet 用例） */}
          {asSheet && effortOpen ? (
            <>
              <button
                type="button"
                data-testid="lulu-effort-backdrop"
                aria-label={t('ui.close')}
                className="lu-model-scrim"
                onClick={closeModelPicker}
              />
            <div
              data-testid="lulu-effort-sheet"
              data-dismiss-outside="yes"
              className="fixed inset-x-0 bottom-0 z-30 rounded-t-npc-card border-t border-npc-lulu-line-2 bg-npc-lulu-bg-2 p-3 shadow-npc-popover"
            >
              <p className="px-1 text-npc-caption font-medium text-npc-lulu-t1">
                <T id="lulu_workbench.model.effort_sheet_title" />
              </p>
              <p className="mb-1 px-1 text-npc-meta text-npc-lulu-t3">
                <T id="lulu_workbench.model.effort_sheet_sub" />
              </p>
              {EFFORTS.map((e) => (
                <button
                  key={e}
                  type="button"
                  role="radio"
                  aria-checked={effort === e}
                  data-testid="lulu-effort-option"
                  data-effort={e}
                  data-active={effort === e ? 'yes' : 'no'}
                  onClick={() => {
                    setEffort(e)
                    setEffortOpen(false)
                  }}
                  className={
                    effort === e
                      ? 'flex min-h-10 w-full items-center justify-between rounded-npc-chip-inline bg-npc-lulu-bg-3 px-2 text-left text-npc-caption font-medium text-npc-lulu-t1'
                      : 'flex min-h-10 w-full items-center justify-between rounded-npc-chip-inline px-2 text-left text-npc-caption text-npc-lulu-t2'
                  }
                >
                  <T id={EFFORT_COPY[e] as 'lulu_workbench.model.effort_fast'} />
                  {effort === e ? <Check aria-hidden className="size-3.5 text-npc-lulu-ok" /> : null}
                </button>
              ))}
              <button
                type="button"
                data-testid="lulu-effort-close"
                className="lu-btn-ghost mt-2 w-full justify-center"
                onClick={() => setEffortOpen(false)}
              >
                <T id="ui.close" />
              </button>
            </div>
            </>
          ) : null}
        </div>

        <div className="flex shrink-0 items-center gap-2">
          <AttachButton items={attachmentItems} onPick={onPickAttachment} />

          <VoiceInput
            onTranscript={(transcript) => {
              const start = ref.current?.selectionStart ?? text.length
              const end = ref.current?.selectionEnd ?? start
              const spacer = start > 0 && !/\s$/.test(text.slice(0, start)) ? ' ' : ''
              const next = text.slice(0, start) + spacer + transcript + text.slice(end)
              const nextCaret = start + spacer.length + transcript.length
              write(next)
              setCaret(nextCaret)
              window.requestAnimationFrame(() => {
                ref.current?.focus()
                ref.current?.setSelectionRange(nextCaret, nextCaret)
              })
            }}
          />

          <button
            type="button"
            data-testid="lulu-send"
            title={asSheet ? undefined : t('lulu_workbench.composer.key_hint')}
            aria-keyshortcuts={asSheet ? undefined : 'Enter'}
            disabled={sending || onSend === undefined || !sendable.ok}
            onClick={() => void submit()}
            className="lu-btn-send shrink-0 disabled:opacity-40"
          >
            <T id={sending ? 'lulu_workbench.composer.sending' : 'lulu_workbench.composer.send'} />
            <span className="lu-btn-aff" aria-hidden>
              <ArrowRight className="size-3" strokeWidth={1.6} />
            </span>
          </button>
        </div>
      </div>

      {/* 发不出去的时候要说清为什么。
          一颗按不动的按钮 + 一句都没有，与「按钮坏了」在屏幕上长得一样。
          **只在「还在传」那一档说**：`empty` 那一档不用说——
          输入框空着这件事他自己看得见，多一句是噪声。 */}
      {sendable.why === 'uploading' ? (
        <p data-testid="lulu-send-blocked" className="mt-1 text-npc-meta text-npc-lulu-t3">
          <T id="lulu_workbench.attach.wait_upload" />
        </p>
      ) : null}

      {/* 发失败了：把话说出来，而且**明说字还在**。
          不说的话用户会以为发出去了，然后等一个不会来的回复。 */}
      {sendFailed ? (
        <p data-testid="lulu-send-failed" className="mt-1 text-npc-meta text-npc-lulu-bad">
          <T id="lulu_workbench.composer.send_failed" />
        </p>
      ) : null}
    </div>
  )
}
